// packages/web/src/components/account/auth-reset-form.tsx
/**
 * Reset-password form — final step of the reset flow.
 *
 * 19BN Phase D D3: opened by the SPA when it sees ?reset-token=… in the URL on
 * load (the reset email points there). User sets a new password + confirms it,
 * we POST /auth/reset-password with the token. On success, switch to login mode
 * so the user signs in with the new password.
 *
 * The token comes from the URL, not a form field — the user never types it.
 *
 * Spec: docs/architecture/account-system-design.md §1.2A
 */

import { useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { authApi } from '../../api/auth.js';
import { IngestApiError } from '../../lib/api-client.js';

interface Props {
  token: string;
  onSwitchMode: (m: 'login') => void;
}

export function AuthResetForm({ token, onSwitchMode }: Props) {
  const t = useT();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (next.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, next);
      // On success, drop the reset-token from the URL and send the user to
      // login with their new password.
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('reset-token');
        window.history.replaceState({}, '', url);
      }
      onSwitchMode('login');
    } catch (err) {
      if (err instanceof IngestApiError && err.status === 401) {
        setError('重置链接已过期或无效，请重新申请');
      } else if (err instanceof IngestApiError && err.status === 400) {
        setError(err.response.message ?? t('auth.weakPassword'));
      } else {
        setError(err instanceof Error ? err.message : '重置失败');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect width="18" height="11" x="3" y="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <h2>设置新密码</h2>
      </div>
      <form className="auth-body" onSubmit={submit}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', margin: '0 0 var(--space-2xl)' }}>
          请为你的账户设置一个新密码（至少 8 位）。
        </p>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-reset-new">{t('auth.newPassword')}</label>
          <input
            id="auth-reset-new"
            className={`auth-field-input${error === t('auth.weakPassword') ? ' has-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-reset-confirm">{t('auth.confirmPassword')}</label>
          <input
            id="auth-reset-confirm"
            className={`auth-field-input${error === t('auth.passwordMismatch') ? ' has-error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        {error && error !== t('auth.weakPassword') && error !== t('auth.passwordMismatch') && (
          <div className="auth-field-error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          重置密码
        </button>
      </form>
    </>
  );
}
